import React, { useEffect, useRef, useState, useCallback } from 'react';
import Header from '../Header';
import plusIcon from '../../assets/icons/ic_plus_blue.svg';
import statusIcon from '../../assets/icons/ic_statuspatient.svg';
import statusIconGrey from '../../assets/icons/ic_status_offline.svg';
import downIcon from '../../assets/icons/ic_arrowdown.svg';
import upIcon from '../../assets/icons/ic_arrowup.svg';
import menuIcon from '../../assets/icons/ic_menu.svg';

import IconTint from 'react-icon-tint';
import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where, Timestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAtom } from 'jotai';
import { currentRobot, isAddUserOpen } from '../store/DataStore';
import { formatDistanceToNow } from 'date-fns';
import NewUserPopup from '../popups/NewUserPopup';
import EditUserPopup from '../popups/EditUserPopup';
import { createUserWithEmailAndPassword, getAuth, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import RemoveUserPopup from '../popups/RemoveUserPopup';
import RobotSerialNumberPopup from '../popups/RobotSerialNumberPopup';
import VersionUploadPopup from '../popups/VersionUploadPopup';

const GRID_COLS =
  'grid grid-cols-[28px_minmax(180px,1.2fr)_minmax(220px,1.6fr)_minmax(170px,1fr)_minmax(170px,1fr)_minmax(130px,0.9fr)_40px]';

const UsersFragment = ({ userName }) => {
  const [matchedUsers, setMatchedUsers] = useState([]);
  const [savedRobot] = useAtom(currentRobot);
  const [addUserStatus, setUserStatus] = useAtom(isAddUserOpen);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [robotPopupStatus, setRobotPopupStatus] = useState(false);
  const [isVersionUploadOpen, setIsVersionUploadOpen] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState('User');

  const isSupportAccount = userName === 'Zeeno Admin' || userName === 'Zeeno Support';
  const isAdminOrSupport = ['Admin', 'Tech Support'].includes(currentUserRole) || isSupportAccount;

  // ---- fetch current user's role
  useEffect(() => {
    const fetchMyRole = async () => {
      const auth = getAuth();
      const me = auth.currentUser;
      if (!me?.email) return;

      const usersCollectionRef = collection(db, 'Companies/Zeeno_Dev/Users');
      const qy = query(usersCollectionRef, where('email', '==', me.email));
      const snap = await getDocs(qy);

      if (!snap.empty) {
        const myDoc = snap.docs[0].data();
        setCurrentUserRole(myDoc.role || 'User');
      }
    };
    fetchMyRole();
  }, []);

  // ---- single source of truth for loading users
  const fetchUsers = useCallback(async () => {
    try {
      const usersCollectionRef = collection(db, 'Companies/Zeeno_Dev/Users');
      const qy = query(usersCollectionRef);
      const querySnapshot = await getDocs(qy);
      const usersData = [];
      querySnapshot.forEach((d) => {
        const userData = d.data();
        if (userData.robots && userData.robots.some((r) => r === savedRobot)) {
          usersData.push(userData);
        }
      });
      setMatchedUsers(usersData);
    } catch (e) {
      console.error('Error fetching users: ', e);
    }
  }, [savedRobot]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const addNewUser = async (fullname, email, phone, password) => {
    const auth = getAuth();
    createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => updateProfile(userCredential.user, { displayName: fullname }))
      .catch((e) => console.error(e));

    try {
      const usersCollectionRef = collection(db, 'Companies/Zeeno_Dev/Users');
      await addDoc(usersCollectionRef, {
        email,
        id: uuidv4(),
        isActive: false,
        lastlogin: Timestamp.fromDate(new Date()),
        name: fullname,
        phone,
        robots: [savedRobot],
        role: 'User',
      });
      await fetchUsers();
    } catch (e) {
      console.error('Error adding user: ', e);
    }
  };

  const handleEdit = (userData) => {
    setEditData(userData);
    setIsEditPopupOpen(true);
  };

  const lastSeenConverter = (lastSeenTimestamp) => {
    if (!lastSeenTimestamp || typeof lastSeenTimestamp.toDate !== 'function') return '—';
    const lastSeenDate = lastSeenTimestamp.toDate();
    return formatDistanceToNow(lastSeenDate, { addSuffix: true });
  };

  async function createRobotData(robotSerialNumber, practiceId) {
    try {
      const data = {
        Actions: { Ping: { pingsetting: { is_online: true } } },
        Settings: {
          Actions: { actionssettings: { area_selected: 'home base', reqattempts_times: 2, reqduration: '180', reqduration_sec: 5, reqfetchattempts_times: 2, reqfetchduration_sec: 5 } },
          Advanced: { advancedsetting: { toggle_topbar: false }, selectedmap: { imageUrl: '' } },
          Battery: { batterysetting: { chargedlevel_percent: 80, chargelevel_percent: 30, toggle_charged: true, warninglevel_percent: 20 } },
          Controller: {
            actions: {
              announce_message: '', current_location: '', currvideo_index: 0, display_message: '',
              escort_name: '', escortfrom_location: '', escortto_location: '',
              fetch_from: '', fetch_item: '', fetch_waittime: '',
              follow_me: false, is_current: false, is_escort: false, is_escorted: false,
              is_fetch: false, is_fetched: false, is_goto: false, is_qrshow: false, is_refresh: false,
              qrcode_title: '', qrcode_url: '', selected_location: '',
              toggle_announce: false, toggle_clear: false, toggle_display: false,
              toggle_message: false, toggle_restart: false, toggle_startvid: false
            },
            settings: {
              app_version: '1.0.1',
              toggle_backuploc: false, toggle_displayface: false, toggle_hardwaredisable: false,
              toggle_letschatmode: false, toggle_obsnotif: false, toggle_restart: false,
              toggle_restoreloc: false, toggle_updatemap: false, toggle_voiceackno: false,
              toggle_voicecommand: false, volume_amount: 2
            },
            videolibrary: { currvideo_index: 0, toggle_startvid: false }
          },
          Hardware: { hardwaresetting: { isGotoActive: false, is_kidmode: false } },
          Integration: { integrationsetting: { bdaycheck_msg: '', earlycheck_msg: '', headercheck_msg: '', latecheck_msg: '', successcheck_msg: '', dynamic_range: '0', check_in_start: '60', check_in_end: '60' } },
          Kiosk: { kiosksetting: { location: '', timeout: 180, toggle_timeout: false } },
          Password: { Kiosk: { exit_password: '0616' }, Video: { password: '0616', toggle_lock: false } },
          bot: { openai: { api_key: '', max_tokens: 9999, model: 'gpt-4o-mini', temperature: 0 }, prompt: { content: '', knowledge: '', timeout: '8' } }
        },
        practiceid: practiceId || '',
      };

      await setDoc(doc(db, `Companies/Zeeno_Dev/Actions/${robotSerialNumber}/Ping/pingsetting`), data.Actions.Ping.pingsetting);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Actions/actionssettings`), data.Settings.Actions.actionssettings);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Advanced/advancedsetting`), data.Settings.Advanced.advancedsetting);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Advanced/selectedmap`), data.Settings.Advanced.selectedmap);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Battery/batterysetting`), data.Settings.Battery.batterysetting);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Controller/actions`), data.Settings.Controller.actions);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Controller/settings`), data.Settings.Controller.settings);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Controller/videolibrary`), data.Settings.Controller.videolibrary);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Hardware/hardwaresetting`), data.Settings.Hardware.hardwaresetting);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Integration/integrationsetting`), data.Settings.Integration.integrationsetting);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Kiosk/kiosksetting`), data.Settings.Kiosk.kiosksetting);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Password/Kiosk`), data.Settings.Password.Kiosk);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/Password/Video`), data.Settings.Password.Video);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/bot/openai`), data.Settings.bot.openai);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Settings/${robotSerialNumber}/bot/prompt`), data.Settings.bot.prompt);
      await setDoc(doc(db, `Companies/Zeeno_Dev/Dolphin/${robotSerialNumber}`), { practiceid: practiceId || '' });
    } catch (e) {
      console.error('Error writing documents: ', e);
    }
  }

  const handleSendRobotSerialNumber = (serialNumber, practiceId) => {
    createRobotData(serialNumber, practiceId);
    setRobotPopupStatus(false);
  };

  return (
    <div className='bg-[#F0F2FF] min-h-screen px-10 py-6'>
      <Header />

      <NewUserPopup isOpen={addUserStatus} onClose={() => setUserStatus(false)} onSend={addNewUser}>
        <h2 className="text-lg font-medium leading-6 text-[#3C5BD2] font-heebo">Add New User</h2>
        <p className="text-md text-gray-800 font-heebo font-light mt-1">{`After sending invitation user will receive registration link via email.`}</p>
      </NewUserPopup>

      <VersionUploadPopup isOpen={isVersionUploadOpen} onClose={() => setIsVersionUploadOpen(false)} db={db} />

      <div className='flex items-center gap-3 w-full 2xl:mt-14 lg:mt-8'>
        <h1 className='font-heebo text-xl text-[#273a84]'>Users</h1>
        <img src={plusIcon} alt="" className="h-6 w-6 object-contain rounded-md cursor-pointer" onClick={() => setUserStatus(true)} />
        {isAdminOrSupport && (
          <>
            <button className="ml-2 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm" onClick={() => setRobotPopupStatus(true)}>
              Add Robot Serial Number
            </button>
            <button className="ml-2 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm" onClick={() => setIsVersionUploadOpen(true)}>
              Upload New Version
            </button>
          </>
        )}
      </div>

      {/* Table container */}
      <div className="mt-5 rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-x-visible overflow-y-visible">
        {/* Header */}
        <div className={`${GRID_COLS} sticky top-0 z-30 bg-white text-xs font-medium uppercase tracking-wide text-slate-600 px-4 py-3`}>
          <span /> {/* status icon column */}
          <span className="pr-4">Name</span>
          <span className="pr-4">Email</span>
          <span className="pr-4">Phone</span>
          <span className="pr-4">Last Seen</span>
          <span className="pr-4">Role</span>
          <span /> {/* menu */}
        </div>

        {/* Rows */}
        <div className="divide-y divide-slate-100">
          {matchedUsers.map((item) => (
            <UserRow
              key={item.id || item.email}
              name={item.name}
              email={item.email}
              phone={item.phone}
              lastSeen={lastSeenConverter(item.lastlogin)}
              isActive={item.isActive ? 'Active' : 'Not Active'}
              role={item.role}
              user={item}
              fetchUsers={fetchUsers}
              onEdit={handleEdit}
              isAdmin={isAdminOrSupport}
            />
          ))}
        </div>
      </div>

      {isEditPopupOpen && editData && (
        <EditUserPopup
          isOpen={isEditPopupOpen}
          user={editData}
          onClose={() => setIsEditPopupOpen(false)}
          onUpdate={() => {
            setIsEditPopupOpen(false);
            fetchUsers();
          }}
        />
      )}

      <RobotSerialNumberPopup isOpen={robotPopupStatus} onClose={() => setRobotPopupStatus(false)} onSend={handleSendRobotSerialNumber} />
    </div>
  );
};

const StatusBadge = ({ active }) => {
  if (active) {
    return (
      <span className="inline-flex items-center justify-center rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-inset ring-slate-300">
      Not Active
    </span>
  );
};

const UserRow = ({ name, email, phone, lastSeen, isActive, role, user, fetchUsers, onEdit, isAdmin }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef();

  // close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const deleteUser = async (userValue) => {
    setUserToDelete(userValue);
    setIsPopupOpen(true);
  };

  const handleConfirmDelete = async () => {
    const usersCollectionRef = collection(db, 'Companies/Zeeno_Dev/Users');
    const userId = userToDelete.id;
    const userQuery = query(usersCollectionRef, where('id', '==', userId));
    let userIDToremove = '';

    const querySnapshot = await getDocs(userQuery);
    querySnapshot.forEach((d) => (userIDToremove = d.id));

    try {
      const userDocRef = doc(db, `Companies/Zeeno_Dev/Users/${userIDToremove}`);
      await deleteDoc(userDocRef);
      await fetchUsers();
    } catch (e) {
      console.error('Error deleting user: ', e);
    } finally {
      setIsPopupOpen(false);
      setUserToDelete(null);
    }
  };

  const resetPassword = async (userValue) => {
    const email = userValue.email;
    if (!email) return alert('Invalid email address');
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      alert('Password reset email sent successfully');
    } catch (e) {
      console.error('Error sending password reset email:', e);
      alert('Password reset email could not be sent');
    } finally {
      setShowMenu(false);
    }
  };

  return (
    <div className={`${GRID_COLS} items-center px-4 py-3 hover:bg-slate-50 transition`}>
      {/* Status icon */}
      <img alt="status" src={isActive === 'Active' ? statusIcon : statusIconGrey} className="h-6 w-6" />

      {/* Name + active badge */}
      <div className="min-w-0">
        <div className="font-heebo text-sm text-slate-900 truncate">{name}</div>
        <div className="mt-0.5">
          <StatusBadge active={isActive === 'Active'} />
        </div>
      </div>

      {/* Email */}
      <div className="min-w-0">
        <div className="font-heebo text-sm text-slate-900 truncate" title={email}>{email}</div>
        <div className="text-xs text-slate-500">Email</div>
      </div>

      {/* Phone */}
      <div className="min-w-0">
        <div className="font-heebo text-sm text-slate-900 truncate" title={phone}>{phone || '—'}</div>
        <div className="text-xs text-slate-500">Phone</div>
      </div>

      {/* Last seen */}
      <div className="min-w-0">
        <div className="font-heebo text-sm text-slate-900 truncate">{lastSeen}</div>
        <div className="text-xs text-slate-500">Last Seen</div>
      </div>

      {/* Role */}
      <div className="min-w-0">
        {isAdmin ? <TypeDropdown role={role} email={email} /> : <div className="font-heebo text-sm text-slate-900">{role}</div>}
      </div>

      {/* Menu */}
      <div className="relative flex justify-end" ref={menuRef}>
        <img
          src={menuIcon}
          alt="menu"
          className="h-6 w-6 cursor-pointer opacity-70 hover:opacity-100"
          onClick={() => setShowMenu((v) => !v)}
        />
        {showMenu && (
          <div className="absolute right-0 top-8 z-50 w-44 rounded-xl border border-slate-100 bg-white p-1 shadow-lg">
            <button className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50" onClick={() => onEdit(user)}>
              Edit
            </button>
            <button className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50" onClick={() => resetPassword(user)}>
              Reset Password
            </button>
            <button className="mt-1 block w-full rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50" onClick={() => deleteUser(user)}>
              Delete User
            </button>
          </div>
        )}
        <RemoveUserPopup isOpen={isPopupOpen} userName={userToDelete?.name} onConfirm={handleConfirmDelete} onCancel={() => { setIsPopupOpen(false); setUserToDelete(null); }} />
      </div>
    </div>
  );
};

const TypeDropdown = ({ role, email }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(role);
  const myComponentRef = useRef(null);
  const list = ['User', 'Admin'];

  useEffect(() => {
    const onDown = (e) => {
      if (myComponentRef.current && !myComponentRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const updateUserRole = async (newRole) => {
    try {
      const usersCollectionRef = collection(db, 'Companies/Zeeno_Dev/Users');
      const qy = query(usersCollectionRef, where('email', '==', email));
      const querySnapshot = await getDocs(qy);
      if (querySnapshot.empty) return;

      const userDocRef = querySnapshot.docs[0].ref;
      await updateDoc(userDocRef, { role: newRole });
      setSelected(newRole);
    } catch (e) {
      console.error('Error updating user role: ', e);
    }
  };

  return (
    <div ref={myComponentRef} className="relative w-fit select-none">
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={`min-w-[110px] pl-2 pr-2 py-1 bg-[#3C5BD2]/10 rounded-xl hover:bg-[#3C5BD2]/5 flex items-center justify-between`}
      >
        <span className="truncate text-[#3C5BD2] font-heebo text-sm">{selected}</span>
        {!isOpen ? <IconTint src={downIcon} className="h-3" color="#3C5BD2" /> : <IconTint src={upIcon} className="h-3 -mt-0.5" color="#3C5BD2" />}
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 z-40 mt-1 w-full rounded-b-md bg-[#e1e6fd] py-1 shadow">
          {list.map((item) => (
            <button
              key={item}
              className="block w-full px-3 py-1.5 text-left text-sm text-[#3C5BD2] hover:bg-[#b9c7ff44]"
              onClick={() => {
                updateUserRole(item);
                setIsOpen(false);
              }}
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UsersFragment;
